import { useEffect, useRef, useState } from "react";
import type { Position } from "./geo";
import { apiFetch } from "./api";

export type Weather = {
  tempC: number | null;
  feelsC: number | null;
  code: number | null;
  windKmh: number | null;
  humidity: number | null;
  isDay: boolean;
  timezone: string | null;
  ts: number;
};

/**
 * useWeather — polls /api/weather every 10 minutes for the current
 * position. The backend caches 10 minutes per ~1 km bucket so multiple
 * tabs / fresh loads don't multiply API calls. Refetches when the user
 * has moved more than ~3 km.
 */
const POLL_MS = 10 * 60 * 1000;
const REFETCH_DISTANCE_M = 3000;

function haversine(aLat: number, aLon: number, bLat: number, bLon: number) {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export function useWeather(position: Position | null): Weather | null {
  const [weather, setWeather] = useState<Weather | null>(null);
  const lastFetchRef = useRef<{ lat: number; lon: number; ts: number } | null>(null);

  // Coarse grid key (~1km) — the effect only re-runs when this changes,
  // so position jitter doesn't trigger refetches but city-scale movement
  // and the initial position arrival do.
  const latKey = position ? position.lat.toFixed(2) : null;
  const lonKey = position ? position.lon.toFixed(2) : null;

  useEffect(() => {
    if (latKey == null || lonKey == null) return;
    const lat = Number(latKey), lon = Number(lonKey);
    let cancelled = false;

    const doFetch = async () => {
      const last = lastFetchRef.current;
      if (last) {
        const aged = Date.now() - last.ts;
        const moved = haversine(lat, lon, last.lat, last.lon);
        if (aged < POLL_MS && moved < REFETCH_DISTANCE_M) return;
      }
      try {
        const r = await apiFetch(`/api/weather?lat=${lat}&lon=${lon}`);
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (cancelled) return;
        setWeather(data);
        lastFetchRef.current = { lat, lon, ts: Date.now() };
      } catch {}
    };

    doFetch();
    const id = window.setInterval(doFetch, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [latKey, lonKey]);

  return weather;
}

/** Map WMO weather code → short label. */
export function weatherLabel(code: number | null, isDay: boolean): string {
  if (code == null) return "—";
  if (code === 0) return isDay ? "Klart" : "Skyfri nat";
  if (code === 1) return isDay ? "For det meste klart" : "Mest skyfri";
  if (code === 2) return "Letskyet";
  if (code === 3) return "Overskyet";
  if (code === 45 || code === 48) return "Tåge";
  if (code >= 51 && code <= 55) return "Støvregn";
  if (code === 56 || code === 57) return "Underafkølet støvregn";
  if (code >= 61 && code <= 65) return "Regn";
  if (code === 66 || code === 67) return "Underafkølet regn";
  if (code >= 71 && code <= 75) return "Sne";
  if (code === 77) return "Sneslud";
  if (code >= 80 && code <= 82) return "Regnbyger";
  if (code === 85 || code === 86) return "Snebyger";
  if (code === 95) return "Tordenvejr";
  if (code === 96 || code === 99) return "Torden med hagl";
  return "—";
}
